import {useState, useContext} from "react";
import {azureTokenContext, userIdContext, messageBoxContext} from "../../App";
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import NewTaskTree from "./NewTaskTree";
import * as ApiCalls from "./ApiCalls.js";
import {WorkItem} from "./ResponsibilityAreaTable";
import {targetUserIdContext} from "../MainPart";
import {useLogMetric} from "../../hooks/useLogMetric";

type NewTaskModalProps = {
    newTask: WorkItem;
    refreshResponsibleArea: boolean;
    setRefreshResponsibleArea: (value: boolean) => void;
};

export default function NewTaskModal({
                                         newTask,
                                         refreshResponsibleArea,
                                         setRefreshResponsibleArea,
                                     }: NewTaskModalProps) {
    const azureToken = useContext(azureTokenContext);
    const userId = useContext(userIdContext);
    const [targetUserId] = useContext(targetUserIdContext);
    const setMessageBox = useContext(messageBoxContext);
    const [open, setOpen] = useState(false);
    const [checkedSubIds, setCheckedSubIds] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const logMetric = useLogMetric();

    const submit = () => {
        setIsSaving(true);

        let data = {
            Source: newTask.Source,
            DocumentId: newTask.DocumentId,
            AuthorId: targetUserId,
            Version: newTask.Version,
            SubIds: checkedSubIds,
            ActorUserId: userId,
        };

        ApiCalls.createWorkitem(data, azureToken)
            .then((response) => {
                if (response.Success === true) {
                    setRefreshResponsibleArea(!refreshResponsibleArea);
                    setMessageBoxFunc("successCreateWorkitem");
                } else {
                    setMessageBoxFunc("errorCreateWorkitem");
                    console.error("Failed create workitem:", response.Message);
                }
            })
            .catch((error) => {
                setMessageBoxFunc("errorCreateWorkitem");
                console.error("Failed create workitem:", error);
            })
            .finally(() => {
                setOpen(false);
                setIsSaving(false);
            });
    };

    const setMessageBoxFunc = (prop: string) => {
        // since it is invocated by changes in prop, I want to make sure that it changes
        setMessageBox("");
        setTimeout(() => {
            setMessageBox(prop);
        }, 10);
    };

    return (
        <div>
            {newTask.ToCs && (newTask.MissingSubIds?.length ?? 0) > 0 && (
                <>
                    <button onClick={() => {
                        setOpen(true);
                        logMetric({category: "NewTask", subCategory: "Modal opened", message: `User opened new task modal for DocumentId: ${newTask.DocumentId}`});
                    }} className="ResponsiveAreaTableButton">
                        Opprett
                    </button>
                    <Dialog open={open} onClose={setOpen} className="relative z-10">
                        <DialogBackdrop
                            transition
                            className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
                        />

                        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div
                                className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <DialogPanel
                                    transition
                                    className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg data-closed:sm:translate-y-0 data-closed:sm:scale-95"
                                >
                                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                                <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                                                    Opprett en ny oppgave
                                                </DialogTitle>
                                                <div className="mt-2">
                                                    <NewTaskTree newTask={newTask} checkedSubIds={checkedSubIds}
                                                                 setCheckedSubIds={setCheckedSubIds}/>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                            type="button"
                                            disabled={isSaving || checkedSubIds.length === 0}
                                            onClick={() => {
                                                logMetric({category: "NewTask", subCategory: "Submitted", message: `User submitted new task for DocumentId: ${newTask.DocumentId}`});
                                                submit();
                                            }}
                                            className=" mt-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-400 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed sm:ml-3 sm:w-auto "
                                        >
                                            {isSaving ? "Lagrer..." : "Lagre"}
                                        </button>
                                        <button
                                            type="button"
                                            data-autofocus
                                            disabled={isSaving}
                                            onClick={() => {
                                                logMetric({category: "NewTask", subCategory: "Modal cancelled", message: `User cancelled new task modal for DocumentId: ${newTask.DocumentId}`});
                                                setOpen(false);
                                            }}
                                            className="mt-3 inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-blue-400 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </DialogPanel>
                            </div>
                        </div>
                    </Dialog>
                </>
            )}
        </div>
    );
}
